import { type LocalRate, type VehicleType } from '../db';

export interface BillingResult {
    total: number;
    breakdown: string;
    durationMinutes: number;
    duracionTexto: string;
}

export const calculateDuration = (entrada: Date, salida: Date) => {
    const diffMs = salida.getTime() - entrada.getTime();
    const diffHoras = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutos = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return {
        text: `${diffHoras}h ${diffMinutos}m`,
        minutes: Math.ceil(diffMs / (1000 * 60))
    };
};

export const calculateRate = (
    entrada: Date | string,
    salida: Date | string,
    _tipoVehiculo: VehicleType | string, // Prefixed with _ to indicate intentionally unused
    rate: LocalRate | null | undefined
): BillingResult => {
    const entryDate = new Date(entrada);
    const exitDate = new Date(salida);
    const { text, minutes } = calculateDuration(entryDate, exitDate);

    // Default result
    let total = 0;
    let breakdown = 'Tarifa no configurada';

    if (!rate) {
        return {
            total: 0,
            breakdown: 'No se encontró tarifa local para este vehículo',
            durationMinutes: minutes,
            duracionTexto: text
        };
    }

    // PASO 1: Verificar si está dentro del tiempo gratis
    if (minutes <= rate.minutosGratis) {
        total = 0;
        breakdown = `Tiempo de gracia (${rate.minutosGratis} min gratis)`;
    } else {
        // PASO 2: Calcular minutos cobrables (restar tiempo gratis)
        const chargeableMinutes = minutes - rate.minutosGratis;

        // PASO 3: Modelo de cobro
        if (chargeableMinutes <= rate.fraccionMinutos) {
            // Caso 1: Dentro de la primera fracción
            total = rate.tarifaBase;
            breakdown = `${minutes} min total - ${rate.minutosGratis} min gratis = ${chargeableMinutes} min cobrables (dentro de primera fracción) → Tarifa Base $${rate.tarifaBase.toLocaleString()}`;
        } else {
            // Caso 2: Excede la primera fracción
            const minutosExcedentes = chargeableMinutes - rate.fraccionMinutos;
            const fraccionesAdicionales = Math.ceil(minutosExcedentes / rate.fraccionMinutos);

            total = rate.tarifaBase + (fraccionesAdicionales * rate.valorFraccion);
            breakdown = `${minutes} min total - ${rate.minutosGratis} min gratis = ${chargeableMinutes} min cobrables → Tarifa Base $${rate.tarifaBase.toLocaleString()} (incluye primera fracción de ${rate.fraccionMinutos} min) + ${fraccionesAdicionales} fracción(es) adicional(es) × $${rate.valorFraccion.toLocaleString()}`;
        }

        // PASO 4: Aplicar tarifa máxima diaria si existe
        if (rate.tarifaMaximaDia > 0 && total > rate.tarifaMaximaDia) {
            total = rate.tarifaMaximaDia;
            breakdown = `Tarifa Máxima Diaria aplicada (${minutes} min total)`;
        }
    }

    return {
        total,
        breakdown,
        durationMinutes: minutes,
        duracionTexto: text
    };
};
